import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAnalyticsOverview,
  getClients,
  getCreditReportAccounts,
  getDocuments,
  getLetters,
} from "../api";

export default function Reports() {
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState({
    summary: {},
    letters_by_day: [],
    tasks_by_status: [],
    disputes_by_bureau: [],
    documents_by_type: [],
  });

  const [clients, setClients] = useState([]);
  const [letters, setLetters] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [creditAccounts, setCreditAccounts] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [message, setMessage] = useState("");

  const loadReports = useCallback(async () => {
    setLoading(true);
    setMessage("");

    try {
      const [
        analyticsData,
        clientsData,
        lettersData,
        documentsData,
      ] = await Promise.all([
        getAnalyticsOverview(),
        getClients(),
        getLetters(),
        getDocuments(),
      ]);

      const clientList = Array.isArray(clientsData) ? clientsData : [];

      setAnalytics({
        summary: analyticsData?.summary || {},
        letters_by_day: Array.isArray(analyticsData?.letters_by_day)
          ? analyticsData.letters_by_day
          : [],
        tasks_by_status: Array.isArray(analyticsData?.tasks_by_status)
          ? analyticsData.tasks_by_status
          : [],
        disputes_by_bureau: Array.isArray(analyticsData?.disputes_by_bureau)
          ? analyticsData.disputes_by_bureau
          : [],
        documents_by_type: Array.isArray(analyticsData?.documents_by_type)
          ? analyticsData.documents_by_type
          : [],
      });

      setClients(clientList);
      setLetters(Array.isArray(lettersData) ? lettersData : []);
      setDocuments(Array.isArray(documentsData) ? documentsData : []);

      if (clientList.length > 0) {
        setSelectedClientId((previous) => {
          const exists = clientList.some(
            (client) => String(client.id) === String(previous)
          );
          return exists ? previous : String(clientList[0].id);
        });
      } else {
        setSelectedClientId("");
      }
    } catch (error) {
      setMessage(error?.message || "Failed to load command center");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClientAccounts = useCallback(async (clientId) => {
    if (!clientId) {
      setCreditAccounts([]);
      return;
    }

    try {
      setLoadingAccounts(true);
      const data = await getCreditReportAccounts(clientId);
      setCreditAccounts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load client credit accounts", error);
      setCreditAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    loadClientAccounts(selectedClientId);
  }, [selectedClientId, loadClientAccounts]);

  const selectedClient = useMemo(
    () =>
      clients.find(
        (client) => String(client.id) === String(selectedClientId)
      ),
    [clients, selectedClientId]
  );

  const selectedClientLetters = useMemo(
    () =>
      letters.filter(
        (letter) =>
          String(letter.client_id) === String(selectedClientId)
      ),
    [letters, selectedClientId]
  );

  const accountSummary = useMemo(() => {
    const summary = {
      total: creditAccounts.length,
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      bankruptcy: 0,
      collection: 0,
      chargeOff: 0,
      latePayment: 0,
    };

    creditAccounts.forEach((account) => {
      const priority = String(account.priority || "Low").toLowerCase();
      const combined = [
        account.account_type,
        account.status,
        account.remarks,
        account.loan_type,
      ]
        .join(" ")
        .toLowerCase();

      if (priority === "critical") summary.critical += 1;
      else if (priority === "high") summary.high += 1;
      else if (priority === "medium") summary.medium += 1;
      else summary.low += 1;

      if (combined.includes("bankruptcy")) summary.bankruptcy += 1;
      if (combined.includes("collection")) summary.collection += 1;
      if (
        combined.includes("charge-off") ||
        combined.includes("charge off") ||
        combined.includes("charged off")
      ) {
        summary.chargeOff += 1;
      }
      if (
        combined.includes("late") ||
        combined.includes("delinquent") ||
        combined.includes("past due")
      ) {
        summary.latePayment += 1;
      }
    });

    return summary;
  }, [creditAccounts]);

  const workflowSummary = useMemo(() => {
    const now = new Date();
    const summary = {
      waiting: 0,
      readyRound2: 0,
      readyRound3: 0,
      escalated: 0,
      deleted: 0,
    };

    selectedClientLetters.forEach((letter) => {
      const status = String(letter.status || "sent").toLowerCase();
      const round = Number(letter.round || 1);
      const nextActionDate = parseDateValue(letter.next_action_date);
      const due =
        nextActionDate &&
        !Number.isNaN(nextActionDate.getTime()) &&
        now >= nextActionDate;

      if (status === "deleted") {
        summary.deleted += 1;
      } else if (status === "escalated" || round >= 3) {
        summary.escalated += 1;
      } else if (status === "sent" && due && round === 1) {
        summary.readyRound2 += 1;
      } else if (status === "sent" && due && round === 2) {
        summary.readyRound3 += 1;
      } else if (status === "sent") {
        summary.waiting += 1;
      }
    });

    return summary;
  }, [selectedClientLetters]);

  const highestPriorityAccounts = useMemo(() => {
    return [...creditAccounts]
      .sort(
        (a, b) =>
          Number(b.risk_score || 0) - Number(a.risk_score || 0)
      )
      .slice(0, 8);
  }, [creditAccounts]);

  const recommendedStrategy = useMemo(() => {
    if (accountSummary.critical > 0) {
      return "Review critical accounts first, confirm supporting evidence, then run AutoPilot on the strongest disputes.";
    }

    if (accountSummary.high > 0) {
      return "Start with high-priority accounts, then process medium-priority accounts after review.";
    }

    if (accountSummary.total > 0) {
      return "Review imported accounts and begin with the strongest accuracy issues.";
    }

    return "Upload a credit report to activate Integrity AI strategy.";
  }, [accountSummary]);

  return (
    <div style={{ padding: "28px", background: "#f3f6fb", minHeight: "100vh" }}>
      <div style={headerRowStyle}>
        <div>
          <div style={eyebrowStyle}>Integrity Suite CRM</div>
          <h1 style={pageTitleStyle}>AI Command Center</h1>
          <div style={pageSubtitleStyle}>
            Client strategy, AutoPilot workflow, and dispute activity in one place.
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button type="button" onClick={loadReports} style={primaryTopButtonStyle}>
            {loading ? "Refreshing..." : "Refresh Center"}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            style={secondaryTopButtonStyle}
          >
            Print Summary
          </button>
        </div>
      </div>

      {message ? <div style={errorMessageStyle}>{message}</div> : null}

      <div style={clientControlPanelStyle}>
        <div style={{ minWidth: "260px", flex: 1 }}>
          <div style={smallLabelStyle}>Active Client</div>
          <select
            value={selectedClientId}
            onChange={(event) => setSelectedClientId(event.target.value)}
            style={clientSelectStyle}
          >
            {clients.length === 0 ? (
              <option value="">No clients available</option>
            ) : (
              clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.full_name || `Client ${client.id}`}
                </option>
              ))
            )}
          </select>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button
            type="button"
            disabled={!selectedClientId}
            onClick={() =>
              navigate(`/letters?client_id=${selectedClientId}`)
            }
            style={actionButtonStyle}
          >
            Open Letters & AutoPilot
          </button>

          <button
            type="button"
            disabled={!selectedClientId}
            onClick={() => navigate(`/clients/${selectedClientId}`)}
            style={secondaryActionButtonStyle}
          >
            Open Client
          </button>
        </div>
      </div>

      <div style={clientBannerStyle}>
        <div>
          <div style={smallLabelStyle}>Command Center Client</div>
          <div style={{ fontSize: "30px", fontWeight: "900", color: "#0f172a" }}>
            {selectedClient?.full_name || "No client selected"}
          </div>
          <div style={{ color: "#64748b", marginTop: "6px" }}>
            {selectedClient?.email || "No email on file"}
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={smallLabelStyle}>Imported Accounts</div>
          <div style={{ fontSize: "44px", fontWeight: "900", color: "#0f172a" }}>
            {loadingAccounts ? "..." : accountSummary.total}
          </div>
        </div>
      </div>

      <div style={summaryGridStyle}>
        <MetricCard title="Critical" value={accountSummary.critical} subtitle="Immediate review" />
        <MetricCard title="High" value={accountSummary.high} subtitle="Strong priority" />
        <MetricCard title="Medium" value={accountSummary.medium} subtitle="Review next" />
        <MetricCard title="Low" value={accountSummary.low} subtitle="Monitor or verify" />
        <MetricCard
          title="Client Letters"
          value={selectedClientLetters.length}
          subtitle="All dispute rounds"
        />
      </div>

      <div style={twoColumnGridStyle}>
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Integrity AI Strategy</div>
          <div style={strategyBoxStyle}>{recommendedStrategy}</div>

          <div style={{ display: "grid", gap: "12px", marginTop: "18px" }}>
            <ReportLine label="Bankruptcy-related" value={accountSummary.bankruptcy} />
            <ReportLine label="Collections" value={accountSummary.collection} />
            <ReportLine label="Charge-offs" value={accountSummary.chargeOff} />
            <ReportLine label="Late-payment issues" value={accountSummary.latePayment} />
          </div>
        </div>

        <div style={panelStyle}>
          <div style={panelHeaderStyle}>AutoPilot Workflow</div>
          <div style={{ display: "grid", gap: "12px" }}>
            <ReportLine label="Waiting for response" value={workflowSummary.waiting} />
            <ReportLine label="Ready for Round 2" value={workflowSummary.readyRound2} />
            <ReportLine label="Ready for Round 3" value={workflowSummary.readyRound3} />
            <ReportLine label="Escalated / Round 3+" value={workflowSummary.escalated} />
            <ReportLine label="Deleted / resolved" value={workflowSummary.deleted} />
          </div>
        </div>
      </div>

      <div style={{ ...panelStyle, marginBottom: "24px" }}>
        <div style={panelHeaderStyle}>Highest-Priority Accounts</div>

        {highestPriorityAccounts.length === 0 ? (
          <div style={emptyStateStyle}>
            No imported accounts are available for this client.
          </div>
        ) : (
          <div style={{ display: "grid", gap: "12px" }}>
            {highestPriorityAccounts.map((account, index) => (
              <div key={account.id || `${account.creditor}-${index}`} style={accountRowStyle}>
                <div>
                  <div style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a" }}>
                    {account.creditor || "Unknown Account"}
                  </div>
                  <div style={{ color: "#64748b", marginTop: "4px" }}>
                    {account.account_number || "N/A"} · {account.bureau || "Unknown Bureau"}
                  </div>
                  <div style={{ color: "#A67C00", marginTop: "6px", fontWeight: "700" }}>
                    {account.recommended_letter || "General Accuracy Dispute"}
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "26px", fontWeight: "900", color: "#0f172a" }}>
                    {Number(account.risk_score || 0)}
                  </div>
                  <div style={{ color: "#64748b", fontWeight: "700" }}>
                    {account.priority || "Low"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={twoColumnGridStyle}>
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Business Snapshot</div>
          <div style={{ display: "grid", gap: "12px" }}>
            <ReportLine label="Total clients" value={analytics.summary?.total_clients || clients.length} />
            <ReportLine label="Total letters" value={analytics.summary?.total_letters || letters.length} />
            <ReportLine label="Total documents" value={analytics.summary?.total_documents || documents.length} />
            <ReportLine label="Workflow items" value={analytics.summary?.total_notes_tasks || 0} />
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, subtitle }) {
  return (
    <div style={metricCardStyle}>
      <div style={smallLabelStyle}>{title}</div>
      <div style={{ fontSize: "44px", fontWeight: "900", color: "#0f172a" }}>
        {value}
      </div>
      <div style={{ color: "#94a3b8", fontSize: "14px" }}>{subtitle}</div>
    </div>
  );
}

function ReportLine({ label, value }) {
  return (
    <div style={reportLineStyle}>
      <div style={{ color: "#64748b", fontWeight: "700" }}>{label}</div>
      <div style={{ color: "#0f172a", fontWeight: "900" }}>{value}</div>
    </div>
  );
}

function parseDateValue(value) {
  if (!value) return null;

  const text = String(value).trim();
  if (!text) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

const headerRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "20px",
  flexWrap: "wrap",
  marginBottom: "28px",
};

const eyebrowStyle = {
  color: "#64748b",
  fontSize: "18px",
  fontWeight: "600",
  marginBottom: "8px",
};

const pageTitleStyle = {
  margin: 0,
  color: "#0f172a",
  fontSize: "58px",
  fontWeight: "900",
  lineHeight: 1,
};

const pageSubtitleStyle = {
  color: "#64748b",
  fontSize: "17px",
  marginTop: "12px",
};

const primaryTopButtonStyle = {
  padding: "16px 24px",
  borderRadius: "16px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "16px",
  fontWeight: "800",
  cursor: "pointer",
};

const secondaryTopButtonStyle = {
  ...primaryTopButtonStyle,
  color: "#0f172a",
  background: "white",
  border: "1px solid #cbd5e1",
};

const clientControlPanelStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "end",
  gap: "18px",
  flexWrap: "wrap",
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "22px",
  padding: "22px",
  marginBottom: "20px",
};

const clientSelectStyle = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid #cbd5e1",
  background: "white",
  fontSize: "16px",
};

const actionButtonStyle = {
  padding: "14px 18px",
  borderRadius: "14px",
  border: "none",
  background: "#D4AF37",
  color: "#111111",
  fontWeight: "800",
  cursor: "pointer",
};

const secondaryActionButtonStyle = {
  ...actionButtonStyle,
  background: "#0f172a",
};

const clientBannerStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "20px",
  alignItems: "center",
  flexWrap: "wrap",
  padding: "24px",
  borderRadius: "24px",
  background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)",
  border: "1px solid #bfdbfe",
  marginBottom: "20px",
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "16px",
  marginBottom: "20px",
};

const twoColumnGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: "20px",
  marginBottom: "24px",
};

const panelStyle = {
  background: "white",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const panelHeaderStyle = {
  fontSize: "28px",
  fontWeight: "900",
  color: "#0f172a",
  marginBottom: "16px",
};

const metricCardStyle = {
  background: "white",
  borderRadius: "20px",
  padding: "20px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 8px 20px rgba(15,23,42,0.05)",
};

const smallLabelStyle = {
  color: "#64748b",
  fontSize: "14px",
  fontWeight: "800",
  marginBottom: "8px",
};

const strategyBoxStyle = {
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#1e3a8a",
  borderRadius: "16px",
  padding: "18px",
  fontSize: "17px",
  fontWeight: "700",
  lineHeight: 1.6,
};

const reportLineStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "14px",
  padding: "14px 16px",
  borderRadius: "14px",
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
};

const accountRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "18px",
  alignItems: "center",
  padding: "16px",
  borderRadius: "16px",
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
};

const errorMessageStyle = {
  border: "2px solid #ef4444",
  borderRadius: "16px",
  padding: "16px 18px",
  marginBottom: "20px",
  background: "#fff",
  color: "#991b1b",
};

const emptyStateStyle = {
  padding: "18px",
  color: "#94a3b8",
  background: "#f8fafc",
  borderRadius: "14px",
  border: "1px dashed #cbd5e1",
};
